import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import React from "react";

import DocumentationDetachmentStatus from "$/settings/enums/machine/template-documentation-status.json";
import {
  ADD_AI_ASSISTANT_DOCUMENTS,
  ADD_AI_ASSISTANT_DOCUMENTS_MUTATION,
  CREATE_AI_ASSISTANT,
  CREATE_AI_ASSISTANT_MUTATION,
  GET_AI_ASSISTANT,
  LIST_AI_ASSISTANTS,
  LIST_AI_ASSISTANTS_QUERY,
  REMVOVE_AI_ASSISTANT,
  REMVOVE_AI_ASSISTANT_MUTATION,
  REMOVE_AI_ASSISTANT_DOCUMENTS,
  REMOVE_AI_ASSISTANT_DOCUMENTS_MUTATION,
  RENAME_AI_ASSISTANT,
  RENAME_AI_ASSISTANT_MUTATION,
  LIST_SHARED_ASSISTANTS,
  LIST_SHARED_AI_ASSISTANTS_QUERY,
  LIST_AI_ASSISTANT_CHATS_QUERY,
  DELETE_AI_ASSISTANT_CHAT_MUTATION,
  RENAME_AI_ASSISTANT_CHAT_MUTATION,
  CREATE_AI_ASSISTANT_CHAT_MUTATION,
  GET_AI_ASSISTANT_CHAT,
  GET_AI_ASSISTANT_CHAT_QUERY,
  LIST_ASSISTANT_DOCUMENTS,
  LIST_ASSISTANT_DOCUMENT_IDS_QUERY,
  LIST_ASSISTANT_DOCUMENTS_QUERY,
  SUBMIT_IDEA_SUGGESTION,
  SUBMIT_IDEA_SUGGESTION_MUTATION,
  GET_AI_ASSISTANT_QUERY,
  UPDATE_AI_ASSISTANT_MUTATION,
} from "~/api";
import { ITEMS_PER_PAGE } from "~/constants/global";
import { DOCUMENT_STATUS } from "~/constants/knowledgebase";
import {
  AiAssistantType,
  DocumentSchemaType,
  AiAssistantChat,
  PaginatedAssistantDocuments,
} from "~/services/ai-assistant/types";
import { errorsHandler } from "~/transformers/_errorsHandler";

// Statuses that mean a document is still moving through the indexing
// pipeline — while any loaded document is in one of these, the documents
// list short-polls for updates.
export const ASSISTANT_DOCUMENT_IN_PROGRESS_STATUSES = [
  DOCUMENT_STATUS.PENDING_VALIDATION,
  DOCUMENT_STATUS.VALIDATING,
  DOCUMENT_STATUS.PENDING_INDEXING,
  DOCUMENT_STATUS.PENDING_SCANNING,
  DOCUMENT_STATUS.SCANNING,
  DOCUMENT_STATUS.PENDING_APPROVAL,
  DOCUMENT_STATUS.INDEXING,
];

const DOCUMENT_STATUS_POLL_INTERVAL_MS = 5000;

type DocumentationDetachmentStatusType =
  (typeof DocumentationDetachmentStatus)[keyof typeof DocumentationDetachmentStatus];

export const useAddAiAssistantDocuments = () => {
  const [mutate, { error, loading }] = useMutation<
    { [ADD_AI_ASSISTANT_DOCUMENTS]: AiAssistantType["_id"] },
    {
      input: {
        _id: AiAssistantType["_id"];
        externalFiles?: Array<
          DocumentSchemaType["externalStorageServiceDocumentID"]
        >;
        internalFiles?: Array<
          DocumentSchemaType["externalStorageServiceDocumentID"]
        >;
      };
    }
  >(ADD_AI_ASSISTANT_DOCUMENTS_MUTATION);

  return {
    addAiAssistantDocuments: mutate,
    addAiAssistantDocumentsError: error,
    addAiAssistantDocumentsInProgress: loading,
  };
};

export const useCreateAiAssistant = () => {
  const [mutate, { loading }] = useMutation<
    { [CREATE_AI_ASSISTANT]: AiAssistantType },
    {
      input: {
        assetId: string;
        assetTemplateId: string;
        description: string;
        externalDocumentIds: string[];
        internalDocumentIds: string[];
        name: string;
        assistantType: string;
      };
    }
  >(CREATE_AI_ASSISTANT_MUTATION);

  return {
    createAiAssistant: async (input: {
      variables: {
        input: {
          name: string;
          description: string;
          assetId: string;
          assetTemplateId: string;
          assistantType: string;
          externalDocumentIds: string[];
          internalDocumentIds: string[];
        };
      };
    }) =>
      new Promise((resolve, reject) => {
        return mutate({
          variables: input.variables,
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getOwnOemAssetById",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    aiAssistantCreationInProgress: loading,
  };
};

export const useGetAiAssistant = (id: AiAssistantType["_id"]) => {
  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery<
    { [GET_AI_ASSISTANT]: AiAssistantType },
    { id: AiAssistantType["_id"] }
  >(GET_AI_ASSISTANT_QUERY, {
    skip: !id,
    variables: { id },
  });

  return {
    assistant: data?.[GET_AI_ASSISTANT],
    error,
    loading,
    refetch,
    // Exposed so the documents view can keep `isIndexAllScheduled` fresh
    // while it explains an empty list with "indexing is scheduled".
    startPolling,
    stopPolling,
  };
};

export const useListAssistantDocuments = ({
  assistantId,
  searchQuery = "",
  visibility,
  limit = ITEMS_PER_PAGE,
  skipCondition = false,
  forcePolling = false,
}: {
  assistantId?: string;
  searchQuery?: string;
  visibility?: string;
  limit?: number;
  skipCondition?: boolean;
  /**
   * Keep polling even when no loaded document is in progress — e.g. while an
   * "index all" walk is pending and documents are about to appear.
   */
  forcePolling?: boolean;
}) => {
  const params = {
    skip: 0,
    limit,
    where: {
      ...(searchQuery ? { searchQuery } : {}),
      ...(visibility ? { visibility } : {}),
    },
  };

  const { client, data, error, loading, fetchMore, refetch } = useQuery<{
    [LIST_ASSISTANT_DOCUMENTS]: PaginatedAssistantDocuments;
  }>(LIST_ASSISTANT_DOCUMENTS_QUERY, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: !assistantId || skipCondition,
    variables: { assistantId, params },
  });

  const result = data?.[LIST_ASSISTANT_DOCUMENTS];
  const documents = result?.documents || [];
  const totalCount = result?.totalCount || 0;

  // Circuit breaker for infinite scroll: if a page comes back with nothing new
  // (all duplicates, or empty because count and list drifted apart), stop
  // asking for more — otherwise `documents.length < totalCount` never resolves
  // and the scroll hook refetches the same page forever.
  const exhaustedRef = React.useRef(false);
  React.useEffect(() => {
    exhaustedRef.current = false;
  }, [assistantId, searchQuery, visibility]);

  const handleFetchMore = React.useCallback(() => {
    if (exhaustedRef.current || documents.length >= totalCount) return;
    fetchMore({
      variables: {
        assistantId,
        params: { ...params, skip: documents.length },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const existing = prev[LIST_ASSISTANT_DOCUMENTS]?.documents || [];
        const incoming =
          fetchMoreResult[LIST_ASSISTANT_DOCUMENTS]?.documents || [];
        // Dedupe by _id in case pages overlap.
        const seen = new Set(existing.map((doc) => doc._id));
        const fresh = incoming.filter((doc) => !seen.has(doc._id));
        if (!fresh.length) exhaustedRef.current = true;
        return {
          [LIST_ASSISTANT_DOCUMENTS]: {
            ...fetchMoreResult[LIST_ASSISTANT_DOCUMENTS],
            documents: [...existing, ...fresh],
          },
        };
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantId, documents.length, totalCount, fetchMore]);

  // Patch a single document in the cache from a PubNub event, or drop it when
  // it was deleted. Status events always carry status/stage/statusReason;
  // title/size/aiGeneratedTitle ride along once the pipeline knows them
  // (documents are created before their Box metadata is fetched and the AI
  // title is generated mid-scan), replacing the pending placeholder. Fields
  // absent from the event leave the cached value untouched, so this also
  // serves metadata-only events (no status).
  const patchDocumentStatus = React.useCallback(
    (message: {
      documentId: string;
      status?: number;
      stage?: string | null;
      stageProgress?: { current: number; total: number } | null;
      statusReason?: string | null;
      title?: string;
      size?: number;
      aiGeneratedTitle?: string | null;
      deleted?: boolean;
    }) => {
      const cached = client.readQuery<{
        [LIST_ASSISTANT_DOCUMENTS]: PaginatedAssistantDocuments;
      }>({
        query: LIST_ASSISTANT_DOCUMENTS_QUERY,
        variables: { assistantId, params },
      });
      const existing = cached?.[LIST_ASSISTANT_DOCUMENTS];
      if (!existing) return;

      const index = existing.documents.findIndex(
        (doc) => doc._id === message.documentId,
      );
      if (index === -1) {
        // Status event for a document we haven't loaded yet (e.g. just-added)
        // — refetch. Metadata-only events for unknown documents are someone
        // else's (e.g. a knowledgebase document on the same channel); ignore.
        if (message.status !== undefined) refetch();
        return;
      }

      const nextDocuments = message.deleted
        ? existing.documents.filter((doc) => doc._id !== message.documentId)
        : existing.documents.map((doc, i) =>
            i === index
              ? {
                  ...doc,
                  status: message.status ?? doc.status,
                  // Status events explicitly send null to clear these; events
                  // without a status (meta updates) leave them untouched.
                  stage:
                    message.status !== undefined
                      ? message.stage ?? null
                      : doc.stage,
                  stageProgress:
                    message.status !== undefined
                      ? message.stageProgress ?? null
                      : doc.stageProgress,
                  statusReason:
                    message.status !== undefined
                      ? message.statusReason ?? null
                      : doc.statusReason,
                  ...(message.title != null ? { title: message.title } : {}),
                  ...(message.size != null ? { size: message.size } : {}),
                  ...(message.aiGeneratedTitle != null
                    ? { aiGeneratedTitle: message.aiGeneratedTitle }
                    : {}),
                }
              : doc,
          );

      client.writeQuery({
        query: LIST_ASSISTANT_DOCUMENTS_QUERY,
        variables: { assistantId, params },
        data: {
          [LIST_ASSISTANT_DOCUMENTS]: {
            ...existing,
            documents: nextDocuments,
            totalCount: message.deleted
              ? Math.max(0, existing.totalCount - 1)
              : existing.totalCount,
          },
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, assistantId, searchQuery, visibility],
  );

  // Short polling replaces PubNub for assistant document status updates: the
  // pipeline persists many small updates per document (stages, per-image and
  // per-chunk counters) and broadcasting each one is costly at PubNub's
  // per-message pricing. Polling only runs while something is actually
  // indexing (or the caller forces it) and stops on its own once every
  // document settles.
  const anyDocumentInProgress = documents.some((doc) =>
    ASSISTANT_DOCUMENT_IN_PROGRESS_STATUSES.includes(doc.status),
  );
  const shouldPoll =
    !!assistantId && !skipCondition && (forcePolling || anyDocumentInProgress);

  React.useEffect(() => {
    if (!shouldPoll) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        // A no-cache snapshot sized to cover every loaded page — writing a
        // plain refetch into the cache would truncate lists the user has
        // scrolled (fetchMore pages live under the first page's variables).
        const { data: snapshot } = await client.query<{
          [LIST_ASSISTANT_DOCUMENTS]: PaginatedAssistantDocuments;
        }>({
          query: LIST_ASSISTANT_DOCUMENTS_QUERY,
          fetchPolicy: "no-cache",
          variables: {
            assistantId,
            params: { ...params, limit: Math.max(documents.length, limit) },
          },
        });
        if (cancelled) return;

        const fresh = snapshot?.[LIST_ASSISTANT_DOCUMENTS];
        if (!fresh) return;

        // New or removed documents (an index-all walk creating them, a Box
        // deletion pruning them) — rebuild the list instead of patching.
        const knownIds = new Set(documents.map((doc) => doc._id));
        const membershipChanged =
          fresh.totalCount !== totalCount ||
          fresh.documents.some((doc) => !knownIds.has(doc._id));
        if (membershipChanged) {
          refetch();
          return;
        }

        fresh.documents.forEach((doc) =>
          patchDocumentStatus({
            documentId: doc._id,
            status: doc.status,
            stage: doc.stage,
            stageProgress: doc.stageProgress,
            statusReason: doc.statusReason,
            title: doc.title,
            size: doc.size,
            aiGeneratedTitle: doc.aiGeneratedTitle,
          }),
        );
      } catch {
        // A failed poll must never break the page — the next tick retries.
      }
    };

    const intervalId = setInterval(poll, DOCUMENT_STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldPoll,
    assistantId,
    documents.length,
    totalCount,
    searchQuery,
    visibility,
  ]);

  return {
    client,
    documents,
    totalCount,
    error,
    loading,
    handleFetchMore,
    hasMore: documents.length < totalCount && !exhaustedRef.current,
    patchDocumentStatus,
    refetch,
  };
};

/**
 * All Box file ids already owned by an assistant, unpaginated — used to
 * exclude them from the "add documents" file picker. The paginated list above
 * can't serve this: it only knows the pages loaded so far.
 */
export const useListAssistantDocumentBoxIds = ({
  assistantId,
  skipCondition = false,
}: {
  assistantId?: string;
  skipCondition?: boolean;
}) => {
  const { data, loading } = useQuery<{
    [LIST_ASSISTANT_DOCUMENTS]: PaginatedAssistantDocuments;
  }>(LIST_ASSISTANT_DOCUMENT_IDS_QUERY, {
    // Fetched fresh each time the picker opens (skipCondition flips) so a
    // just-indexed document can't be re-picked.
    fetchPolicy: "network-only",
    skip: !assistantId || skipCondition,
    variables: { assistantId },
  });

  return {
    boxDocumentIds: (data?.[LIST_ASSISTANT_DOCUMENTS]?.documents || []).map(
      (document) => document.boxDocumentId,
    ),
    loading,
  };
};

export const useListAiAssistants = ({
  limit,
  skipCondition,
  searchQuery,
}: { limit?: number; skipCondition?: boolean; searchQuery?: string } = {}) => {
  const { client, data, error, loading } = useQuery<{
    [LIST_AI_ASSISTANTS]: {
      aiAssistants: Array<AiAssistantType>;
      totalCount: number;
    };
  }>(LIST_AI_ASSISTANTS_QUERY, {
    skip: skipCondition,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: { searchQuery, ...(limit ? { limit } : {}) },
  });

  const addAiAssistant = React.useCallback(
    (aiAssistant: AiAssistantType) => {
      const existingData = client.readQuery<{
        [LIST_AI_ASSISTANTS]: {
          aiAssistants: Array<AiAssistantType>;
          totalCount: number;
        };
      }>({
        query: LIST_AI_ASSISTANTS_QUERY,
      });
      client.writeQuery<{
        [LIST_AI_ASSISTANTS]: {
          aiAssistants: Array<AiAssistantType>;
          totalCount: number;
        };
      }>({
        data: {
          [LIST_AI_ASSISTANTS]: {
            aiAssistants: [
              ...(existingData?.[LIST_AI_ASSISTANTS].aiAssistants || []),
              aiAssistant,
            ],
            totalCount: existingData?.[LIST_AI_ASSISTANTS].totalCount + 1,
          },
        },
        query: LIST_AI_ASSISTANTS_QUERY,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const removeAiAssistant = React.useCallback(
    (aiAssistantID: AiAssistantType["_id"]) => {
      const existingData = client.readQuery<{
        [LIST_AI_ASSISTANTS]: {
          aiAssistants: Array<AiAssistantType>;
          totalCount: number;
        };
      }>({
        query: LIST_AI_ASSISTANTS_QUERY,
        variables: { searchQuery },
      });
      client.writeQuery<{
        [LIST_AI_ASSISTANTS]: {
          aiAssistants: Array<AiAssistantType>;
          totalCount: number;
        };
      }>({
        data: {
          [LIST_AI_ASSISTANTS]: {
            aiAssistants: existingData[LIST_AI_ASSISTANTS].aiAssistants.filter(
              (aiAssistant) => aiAssistant._id !== aiAssistantID,
            ),
            totalCount: existingData?.[LIST_AI_ASSISTANTS].totalCount,
          },
        },
        query: LIST_AI_ASSISTANTS_QUERY,
        variables: { searchQuery },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const renameAiAssistant = React.useCallback(
    (
      aiAssistantID: AiAssistantType["_id"],
      newMacineAssistantName: AiAssistantType["assistantName"],
    ) => {
      const existingData = client.readQuery<{
        [LIST_AI_ASSISTANTS]: {
          aiAssistants: Array<AiAssistantType>;
          totalCount: number;
        };
      }>({
        query: LIST_AI_ASSISTANTS_QUERY,
      });
      const existingAiAssistantIndex = existingData[
        LIST_AI_ASSISTANTS
      ].aiAssistants.findIndex(
        (aiAssistant) => aiAssistant._id === aiAssistantID,
      );
      client.writeQuery<{
        [LIST_AI_ASSISTANTS]: {
          aiAssistants: Array<AiAssistantType>;
          totalCount: number;
        };
      }>({
        data: {
          [LIST_AI_ASSISTANTS]: {
            aiAssistants: [
              ...existingData[LIST_AI_ASSISTANTS].aiAssistants.slice(
                0,
                existingAiAssistantIndex,
              ),
              {
                ...existingData[LIST_AI_ASSISTANTS][existingAiAssistantIndex],
                assistantName: newMacineAssistantName,
              },
              ...existingData[LIST_AI_ASSISTANTS].aiAssistants.slice(
                existingAiAssistantIndex + 1,
              ),
            ],
            totalCount: existingData[LIST_AI_ASSISTANTS].totalCount,
          },
        },
        query: LIST_AI_ASSISTANTS_QUERY,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const updateDetachedFromTemplate = React.useCallback(
    (
      aiAssistantID: AiAssistantType["_id"],
      documentationDetachedFromTemplateStatus: DocumentationDetachmentStatusType,
    ) => {
      const existingData = client.readQuery<{
        [LIST_AI_ASSISTANTS]: {
          aiAssistants: Array<AiAssistantType>;
          totalCount: number;
        };
      }>({
        query: LIST_AI_ASSISTANTS_QUERY,
      });

      const existingAiAssistantIndex = existingData[
        LIST_AI_ASSISTANTS
      ].aiAssistants.findIndex(
        (aiAssistant) => aiAssistant._id === aiAssistantID,
      );

      if (existingAiAssistantIndex)
        client.writeQuery<{
          [LIST_AI_ASSISTANTS]: {
            aiAssistants: Array<AiAssistantType>;
            totalCount: number;
          };
        }>({
          data: {
            [LIST_AI_ASSISTANTS]: {
              aiAssistants: [
                ...existingData[LIST_AI_ASSISTANTS].aiAssistants.slice(
                  0,
                  existingAiAssistantIndex,
                ),
                {
                  ...existingData[LIST_AI_ASSISTANTS][existingAiAssistantIndex],
                  machine: {
                    ...existingData[LIST_AI_ASSISTANTS][
                      existingAiAssistantIndex
                    ].machine,
                    detachedFromTemplate: {
                      documentation: documentationDetachedFromTemplateStatus,
                    },
                  },
                  ...(documentationDetachedFromTemplateStatus ===
                  DocumentationDetachmentStatus.detached
                    ? {
                        documents: {
                          externalDocuments: [],
                          internalDocuments: [],
                        },
                      }
                    : {}),
                },
                ...existingData[LIST_AI_ASSISTANTS].aiAssistants.slice(
                  existingAiAssistantIndex + 1,
                ),
              ],
              totalCount: existingData[LIST_AI_ASSISTANTS].totalCount,
            },
          },
          query: LIST_AI_ASSISTANTS_QUERY,
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    addAiAssistant,
    listAiAssistantsError: error,
    listAiAssistantsInProgress: loading,
    aiAssistants: data?.[LIST_AI_ASSISTANTS]?.aiAssistants,
    totalCount: data?.[LIST_AI_ASSISTANTS]?.totalCount,
    removeAiAssistant,
    renameAiAssistant,
    updateDetachedFromTemplate,
    error,
  };
};

export const useGetAiAssistantChat = (chatId: string) => {
  const { client, data, error, loading } = useQuery<
    { [GET_AI_ASSISTANT_CHAT]: AiAssistantChat },
    { id: AiAssistantChat["_id"] }
  >(GET_AI_ASSISTANT_CHAT_QUERY, {
    skip: !chatId,
    variables: { id: chatId },
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });

  return {
    client,
    data: data?.[GET_AI_ASSISTANT_CHAT],
    error,
    loading,
  };
};

export const useListAiAssistantChats = ({
  limit = ITEMS_PER_PAGE,
  skip = 0,
  skipCondition = false,
  sort = [],
  where = {},
}: {
  limit?: number;
  skip?: number;
  skipCondition?: boolean;
  sort?: Array<string>;
  where?: Record<string, unknown>;
}) => {
  const {
    data: { listAiAssistantChats } = { listAiAssistantChats: {} },
    loading,
    error,
    refetch,
    fetchMore,
  } = useQuery(LIST_AI_ASSISTANT_CHATS_QUERY, {
    variables: {
      params: {
        limit,
        skip,
        sort,
        where: {
          ...where,
        },
      },
    },
    skip: skipCondition,
  });

  const {
    aiAssistantChats = [],
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
  } = listAiAssistantChats;

  const handleFetchMore = ({ limit, skip }) => {
    fetchMore({
      variables: {
        params: {
          limit,
          skip,
          sort,
          where: {
            ...where,
          },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedAiAssistantChats = [
          ...previousResult.listAiAssistantChats.aiAssistantChats,
          ...fetchMoreResult.listAiAssistantChats.aiAssistantChats,
        ];

        const updates = {
          ...previousResult,
          listAiAssistantChats: {
            ...previousResult.listAiAssistantChats,
            ...fetchMoreResult.listAiAssistantChats,
            aiAssistantChats: mergedAiAssistantChats,
          },
        };

        return updates;
      },
    });
  };

  return {
    aiAssistantChats,
    currentPage,
    error,
    handleFetchMore,
    limit: itemLimit,
    loading,
    refetch,
    skip: itemSkip,
    totalCount,
  };
};

export const useCreateAiAssistantChat = () => {
  const [createAiAssistantChat, { loading, error }] = useMutation(
    CREATE_AI_ASSISTANT_CHAT_MUTATION,
  );

  return {
    isCreatingChat: loading,
    error,
    createAiAssistantChat: async ({ id }: { id: string }) =>
      new Promise((resolve, reject) => {
        return createAiAssistantChat({
          variables: { aiAssistantId: id },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAiAssistantChats",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useDeleteAiAssistantChat = () => {
  const [deleteAiAssistantChat, { loading, error }] = useMutation(
    DELETE_AI_ASSISTANT_CHAT_MUTATION,
  );

  return {
    isDeleting: loading,
    error,
    deleteAiAssistantChat: async ({ id }: { id: string }) =>
      new Promise((resolve, reject) => {
        return deleteAiAssistantChat({
          variables: { aiAssistantChatId: id },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAiAssistantChats",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useRenameAiAssistantChat = () => {
  const [renameAiAssistantChat, { loading, error }] = useMutation(
    RENAME_AI_ASSISTANT_CHAT_MUTATION,
  );

  return {
    isRenaming: loading,
    error,
    renameAiAssistantChat: async (input) =>
      new Promise((resolve, reject) => {
        return renameAiAssistantChat({
          variables: { input },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAiAssistantChats",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useUpdateAiAssistant = () => {
  const [updateAiAssistant, { loading, error }] = useMutation<
    { updateAiAssistant: Document },
    {
      input: {
        _id: string;
        assistantName: string;
        description: string;
      };
    }
  >(UPDATE_AI_ASSISTANT_MUTATION);

  return {
    isUpdating: loading,
    error,
    updateAiAssistant: async (input: {
      _id: string;
      assistantName: string;
      description: string;
    }) =>
      new Promise((resolve, reject) => {
        return updateAiAssistant({
          variables: { input },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getAiAssistant",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useRemoveAiAssistant = () => {
  const [mutate, { error, loading }] = useMutation<
    { [REMVOVE_AI_ASSISTANT]: string },
    { aiAssistantID: AiAssistantType["_id"] }
  >(REMVOVE_AI_ASSISTANT_MUTATION);

  return {
    removeAiAssistant: async ({ aiAssistantID }: { aiAssistantID: string }) =>
      new Promise((resolve, reject) => {
        return mutate({
          variables: { aiAssistantID },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getOwnOemAssetById",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    removeAiAssistantError: error,
    removeAiAssistantInProgress: loading,
  };
};

export const useRemoveAiAssistantDocuments = () => {
  const [mutate, { error, loading }] = useMutation<
    { [REMOVE_AI_ASSISTANT_DOCUMENTS]: AiAssistantType["_id"] },
    {
      input: {
        _id: AiAssistantType["_id"];
        documentIds: Array<DocumentSchemaType["_id"]>;
      };
    }
  >(REMOVE_AI_ASSISTANT_DOCUMENTS_MUTATION);

  return {
    removeAiAssistantDocuments: mutate,
    removeAiAssistantDocumentsError: error,
    removeAiAssistantDocumentsInProgress: loading,
  };
};

export const useRenameAiAssistant = () => {
  const [mutate, { error, loading }] = useMutation<
    { [RENAME_AI_ASSISTANT]: AiAssistantType["_id"] },
    { input: Partial<AiAssistantType> }
  >(RENAME_AI_ASSISTANT_MUTATION);

  return {
    renameAiAssistant: mutate,
    renameAiAssistantError: error,
    renameAiAssistantInProgress: loading,
  };
};

export const useListSharedAssistants = ({
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery = "",
  skipCondition = false,
  companyId,
  isPreview = false,
}: {
  limit?: number;
  skip?: number;
  searchQuery?: string;
  skipCondition?: boolean;
  companyId?: string;
  isPreview?: boolean;
} = {}) => {
  const variables = {
    params: {
      limit,
      skip,
      where: {
        searchQuery,
        ...(companyId && { companyId }),
        isPreview,
      },
    },
  };

  const { data, loading, refetch, fetchMore } = useQuery<
    {
      [LIST_SHARED_ASSISTANTS]: {
        currentPage: number;
        limit: number;
        skip: number;
        totalCount: number;
        assistants: AiAssistantType[];
      };
    },
    {
      params: {
        limit: number;
        skip: number;
        where: {
          searchQuery: string;
        };
      };
    }
  >(LIST_SHARED_AI_ASSISTANTS_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    assistants = [],
  } = data?.[LIST_SHARED_ASSISTANTS] ?? {
    currentPage: 0,
    limit: 0,
    skip: 0,
    totalCount: 0,
    assistants: [],
  };

  const handleFetchMore = ({
    limit,
    skip,
  }: {
    limit: number;
    skip: number;
  }): void => {
    fetchMore({
      variables: {
        params: {
          limit,
          skip,
          where: {
            searchQuery,
            ...(companyId && { companyId }),
          },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedAssistants = [
          ...previousResult[LIST_SHARED_ASSISTANTS].assistants,
          ...fetchMoreResult[LIST_SHARED_ASSISTANTS].assistants,
        ];

        return {
          ...previousResult,
          listSharedAiAssistants: {
            ...previousResult[LIST_SHARED_ASSISTANTS],
            ...fetchMoreResult[LIST_SHARED_ASSISTANTS],
            assistants: mergedAssistants,
          },
        };
      },
    });
  };

  return {
    assistants,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};

export const useSuggestAssistantIdeas = () => {
  const [mutate, { error, loading }] = useMutation<
    { [SUBMIT_IDEA_SUGGESTION]: string },
    { suggestion: string }
  >(SUBMIT_IDEA_SUGGESTION_MUTATION);

  return {
    suggestIdea: mutate,
    error,
    loading,
  };
};
